/**
 * AxLabs Physics Experiments - Mechanics
 *
 * Collection of classical mechanics experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema, STANDARD_GRAVITY } from '../../framework/index.js';
import { getParam, getState, getStateValues } from './helpers.js';
// ============================================================================
// ATWOOD MACHINE
// ============================================================================
export const AtwoodMachine = createExperimentFromSchema({
    id: 'atwood-machine',
    name: 'Atwood Machine',
    domain: 'physics',
    description: 'Classic Atwood machine: two masses suspended over a frictionless pulley',
    conceptTags: ['Newton\'s Laws', 'Tension', 'Pulley Systems', 'Acceleration'],
    difficulty: 4,
    stateVariables: [
        { id: 'y1', name: 'Mass 1 Position', unit: 'm', initial: 0, symbol: 'y_1', description: 'Vertical position of mass 1 (positive upward)' },
        { id: 'v1', name: 'Mass 1 Velocity', unit: 'm/s', initial: 0, symbol: 'v_1', description: 'Velocity of mass 1' },
    ],
    parameters: [
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 0.1, max: 10, default: 2, symbol: 'm_1', description: 'Mass of object 1 (left side)' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 0.1, max: 10, default: 3, symbol: 'm_2', description: 'Mass of object 2 (right side)' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g', description: 'Gravitational acceleration' },
    ],
    equations: [
        {
            id: 'acceleration',
            name: 'System Acceleration',
            latex: 'a = \\frac{m_2 - m_1}{m_1 + m_2}g',
            description: 'Acceleration of the system (positive = m2 moves down)',
        },
        {
            id: 'tension',
            name: 'Tension in Rope',
            latex: 'T = \\frac{2m_1m_2}{m_1 + m_2}g',
            description: 'Tension force in the rope',
        },
    ],
    derivatives: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const g = getParam(params, 'g');
        return {
            y1: getState(state, 'v1'),
            v1: ((m2 - m1) * g) / (m1 + m2),
        };
    },
    energyComputation: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const g = getParam(params, 'g');
        const y1 = getState(state, 'y1');
        const v1 = getState(state, 'v1');
        const y2 = -y1;
        const v2 = -v1;
        const ke1 = 0.5 * m1 * v1 * v1;
        const ke2 = 0.5 * m2 * v2 * v2;
        const pe1 = m1 * g * y1;
        const pe2 = m2 * g * y2;
        return {
            kinetic: ke1 + ke2,
            potential: pe1 + pe2,
            thermal: 0,
            total: ke1 + ke2 + pe1 + pe2,
        };
    },
    failureModes: [
        {
            id: 'mass-zero',
            name: 'Zero Mass',
            description: 'Mass cannot be zero',
            check: (_state, params) => {
                const m1 = getParam(params, 'm1');
                const m2 = getParam(params, 'm2');
                return m1 <= 0 || m2 <= 0;
            },
            severity: 'error',
            educationalMessage: 'Mass must be positive for the Atwood machine to function. The acceleration formula involves division by total mass (m₁ + m₂).',
        },
        {
            id: 'rope-snap',
            name: 'Rope Tension Limit',
            description: 'Tension exceeds rope strength',
            check: (_state, params) => {
                const m1 = getParam(params, 'm1');
                const m2 = getParam(params, 'm2');
                const g = getParam(params, 'g');
                const tension = (2 * m1 * m2 / (m1 + m2)) * g;
                return tension > 1000;
            },
            severity: 'critical',
            educationalMessage: 'The tension in the rope has exceeded its breaking strength! T = 2m₁m₂g/(m₁+m₂). Use lighter masses or a stronger rope.',
        },
    ],
    bounds: {
        y1: { min: -10, max: 10 },
        v1: { min: -20, max: 20 },
    },
});
// ============================================================================
// DOUBLE ATWOOD MACHINE
// ============================================================================
export const DoubleAtwoodMachine = createExperimentFromSchema({
    id: 'double-atwood-machine',
    name: 'Double Atwood Machine',
    domain: 'physics',
    description: 'Compound pulley system with one movable pulley',
    conceptTags: ['Newton\'s Laws', 'Constraint Forces', 'Multi-body Dynamics'],
    difficulty: 7,
    stateVariables: [
        { id: 'y1', name: 'Mass 1 Position', unit: 'm', initial: 0, symbol: 'y_1', description: 'Position of mass 1 (hanging from movable pulley)' },
        { id: 'yM', name: 'Movable Pulley Position', unit: 'm', initial: 0, symbol: 'y_M', description: 'Position of the movable pulley' },
        { id: 'v1', name: 'Mass 1 Velocity', unit: 'm/s', initial: 0, symbol: 'v_1' },
        { id: 'vM', name: 'Movable Pulley Velocity', unit: 'm/s', initial: 0, symbol: 'v_M' },
    ],
    parameters: [
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm_1' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 0.1, max: 10, default: 2, symbol: 'm_2', description: 'Mass on movable pulley with m1' },
        { id: 'm3', name: 'Mass 3', unit: 'kg', min: 0.1, max: 10, default: 3, symbol: 'm_3', description: 'Mass on fixed pulley side' },
        { id: 'M', name: 'Movable Pulley Mass', unit: 'kg', min: 0.1, max: 5, default: 0.5, symbol: 'M' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'constraint1',
            name: 'Rope Length Constraint 1',
            latex: 'y_1 + y_2 - 2y_M = \\text{constant}',
            description: 'Rope over movable pulley constraint',
        },
        {
            id: 'constraint2',
            name: 'Rope Length Constraint 2',
            latex: 'y_M + y_3 = \\text{constant}',
            description: 'Rope over fixed pulley constraint',
        },
    ],
    derivatives: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const m3 = getParam(params, 'm3');
        const M = getParam(params, 'M');
        const g = getParam(params, 'g');
        const v1 = getState(state, 'v1');
        const vM = getState(state, 'vM');
        const denom = 4 * m1 * m2 + m1 * m3 + m2 * m3 + M * (m1 + m2);
        const aM_num = -g * (4 * m1 * m2 - m1 * m3 - m2 * m3 - 2 * M * (m1 + m2));
        const aM = aM_num / denom;
        const a1_num = g * (2 * m1 * m2 - 3 * m1 * m3 + m2 * m3 - 2 * M * (m1 - m2));
        const a1 = a1_num / denom;
        return {
            y1: v1,
            v1: a1,
            yM: vM,
            vM: aM,
        };
    },
    failureModes: [
        {
            id: 'mass-zero',
            name: 'Zero Mass',
            check: (_state, params) => {
                const m1 = getParam(params, 'm1');
                const m2 = getParam(params, 'm2');
                const m3 = getParam(params, 'm3');
                const M = getParam(params, 'M');
                return m1 <= 0 || m2 <= 0 || m3 <= 0 || M <= 0;
            },
            severity: 'error',
            educationalMessage: 'All masses must be positive for physical validity.',
        },
    ],
    bounds: {
        y1: { min: -5, max: 5 },
        yM: { min: -5, max: 5 },
    },
});
// ============================================================================
// INCLINED PLANE WITH FRICTION
// ============================================================================
export const InclinedPlaneFriction = createExperimentFromSchema({
    id: 'inclined-plane-friction',
    name: 'Inclined Plane with Friction',
    domain: 'physics',
    description: 'Block sliding down an inclined plane with kinetic friction',
    conceptTags: ['Friction', 'Normal Force', 'Inclined Plane', 'Decomposition of Forces'],
    difficulty: 3,
    stateVariables: [
        { id: 'x', name: 'Position Down Slope', unit: 'm', initial: 0, symbol: 'x', description: 'Position along the incline (measured from top)' },
        { id: 'v', name: 'Velocity', unit: 'm/s', initial: 0, symbol: 'v', description: 'Velocity down the slope' },
    ],
    parameters: [
        { id: 'theta', name: 'Incline Angle', unit: 'rad', min: 0, max: Math.PI / 2, default: Math.PI / 6, symbol: '\\theta', description: 'Angle of incline from horizontal' },
        { id: 'mu_k', name: 'Kinetic Friction Coeff', unit: '', min: 0, max: 1, default: 0.3, symbol: '\\mu_k', description: 'Coefficient of kinetic friction' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'acceleration',
            name: 'Down-Slope Acceleration',
            latex: 'a = g(\\sin\\theta - \\mu_k\\cos\\theta)',
            description: 'Acceleration when sliding',
        },
        {
            id: 'normal-force',
            name: 'Normal Force',
            latex: 'N = mg\\cos\\theta',
            description: 'Normal force perpendicular to surface',
        },
    ],
    derivatives: (state, params) => {
        const theta = getParam(params, 'theta');
        const mu_k = getParam(params, 'mu_k');
        const g = getParam(params, 'g');
        const sinTheta = Math.sin(theta);
        const cosTheta = Math.cos(theta);
        const frictionTerm = mu_k * cosTheta;
        const a = Math.max(0, g * (sinTheta - frictionTerm));
        return {
            x: getState(state, 'v'),
            v: a,
        };
    },
    energyComputation: (state, params) => {
        const theta = getParam(params, 'theta');
        const mu_k = getParam(params, 'mu_k');
        const g = getParam(params, 'g');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const ke = 0.5 * v * v;
        const h = -x * Math.sin(theta);
        const pe = g * h;
        const workFriction = mu_k * g * Math.cos(theta) * Math.max(0, x);
        return {
            kinetic: ke,
            potential: pe,
            thermal: workFriction,
            total: ke + pe,
        };
    },
    failureModes: [
        {
            id: 'no-motion',
            name: 'Block Does Not Slide',
            check: (_state, params) => {
                const theta = getParam(params, 'theta');
                const mu_k = getParam(params, 'mu_k');
                return Math.tan(theta) <= mu_k;
            },
            severity: 'warning',
            educationalMessage: 'The block will not slide because tan(θ) ≤ μₖ. Increase the incline angle or reduce friction.',
        },
        {
            id: 'invalid-friction',
            name: 'Invalid Friction Coefficient',
            check: (_state, params) => {
                const mu_k = getParam(params, 'mu_k');
                return mu_k < 0 || mu_k > 1;
            },
            severity: 'error',
            educationalMessage: 'Coefficient of friction must be between 0 and 1 for most materials.',
        },
    ],
    bounds: {
        x: { min: 0, max: 50 },
        v: { min: 0, max: 30 },
    },
});
// ============================================================================
// ELASTIC COLLISION 1D
// ============================================================================
export const ElasticCollision1D = createExperimentFromSchema({
    id: 'elastic-collision-1d',
    name: '1D Elastic Collision',
    domain: 'physics',
    description: 'Perfectly elastic collision between two objects in one dimension',
    conceptTags: ['Conservation of Momentum', 'Conservation of Energy', 'Collisions'],
    difficulty: 4,
    stateVariables: [
        { id: 'x1', name: 'Object 1 Position', unit: 'm', initial: -2, symbol: 'x_1' },
        { id: 'x2', name: 'Object 2 Position', unit: 'm', initial: 0, symbol: 'x_2' },
        { id: 'v1', name: 'Object 1 Velocity', unit: 'm/s', initial: 2, symbol: 'v_1' },
        { id: 'v2', name: 'Object 2 Velocity', unit: 'm/s', initial: 0, symbol: 'v_2' },
        { id: 'collided', name: 'Collision Flag', unit: '', initial: 0, description: 'Flag to track if collision occurred' },
    ],
    parameters: [
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm_1' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 0.1, max: 10, default: 2, symbol: 'm_2' },
        { id: 'radius', name: 'Object Radius', unit: 'm', min: 0.01, max: 1, default: 0.1, symbol: 'R' },
    ],
    equations: [
        {
            id: 'final-v1',
            name: 'Final Velocity 1',
            latex: 'v_1\' = \\frac{m_1 - m_2}{m_1 + m_2}v_1 + \\frac{2m_2}{m_1 + m_2}v_2',
            description: 'Velocity of m1 after collision',
        },
        {
            id: 'final-v2',
            name: 'Final Velocity 2',
            latex: 'v_2\' = \\frac{2m_1}{m_1 + m_2}v_1 + \\frac{m_2 - m_1}{m_1 + m_2}v_2',
            description: 'Velocity of m2 after collision',
        },
    ],
    derivatives: (state, params) => {
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const v1 = getState(state, 'v1');
        const v2 = getState(state, 'v2');
        const collided = getState(state, 'collided');
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const radius = getParam(params, 'radius');
        const distance = x2 - x1;
        const collisionThreshold = 2 * radius;
        if (distance <= collisionThreshold && v1 > v2 && collided < 0.5) {
            const v1_f = ((m1 - m2) * v1 + 2 * m2 * v2) / (m1 + m2);
            const v2_f = (2 * m1 * v1 + (m2 - m1) * v2) / (m1 + m2);
            return {
                x1: v1_f,
                x2: v2_f,
                v1: 0,
                v2: 0,
                collided: 1,
            };
        }
        const resetCollision = collided > 0.5 && distance > collisionThreshold * 1.1;
        return {
            x1: v1,
            x2: v2,
            v1: 0,
            v2: 0,
            collided: resetCollision ? 0 : collided,
        };
    },
    energyComputation: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const v1 = getState(state, 'v1');
        const v2 = getState(state, 'v2');
        const ke = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v2 * v2;
        return { kinetic: ke, potential: 0, thermal: 0, total: ke };
    },
    failureModes: [
        {
            id: 'negative-mass',
            name: 'Negative Mass',
            check: (_state, params) => {
                const m1 = getParam(params, 'm1');
                const m2 = getParam(params, 'm2');
                return m1 <= 0 || m2 <= 0;
            },
            severity: 'error',
            educationalMessage: 'Mass must be positive for physical validity.',
        },
    ],
    bounds: {
        x1: { min: -10, max: 10 },
        x2: { min: -10, max: 10 },
        v1: { min: -20, max: 20 },
        v2: { min: -20, max: 20 },
    },
});
// ============================================================================
// INELASTIC COLLISION 1D
// ============================================================================
export const InelasticCollision1D = createExperimentFromSchema({
    id: 'inelastic-collision-1d',
    name: '1D Inelastic Collision',
    domain: 'physics',
    description: 'Partially inelastic collision between two objects in one dimension',
    conceptTags: ['Conservation of Momentum', 'Coefficient of Restitution', 'Energy Loss'],
    difficulty: 5,
    stateVariables: [
        { id: 'x1', name: 'Object 1 Position', unit: 'm', initial: -2, symbol: 'x_1' },
        { id: 'x2', name: 'Object 2 Position', unit: 'm', initial: 0, symbol: 'x_2' },
        { id: 'v1', name: 'Object 1 Velocity', unit: 'm/s', initial: 3, symbol: 'v_1' },
        { id: 'v2', name: 'Object 2 Velocity', unit: 'm/s', initial: 0, symbol: 'v_2' },
        { id: 'collided', name: 'Collision Flag', unit: '', initial: 0 },
    ],
    parameters: [
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 0.1, max: 10, default: 1.5, symbol: 'm_1' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm_2' },
        { id: 'e', name: 'Coefficient of Restitution', unit: '', min: 0, max: 1, default: 0.5, symbol: 'e', description: 'e=1: elastic, e=0: perfectly inelastic' },
        { id: 'radius', name: 'Object Radius', unit: 'm', min: 0.01, max: 1, default: 0.1, symbol: 'R' },
    ],
    equations: [
        {
            id: 'restitution',
            name: 'Coefficient of Restitution',
            latex: 'e = \\frac{v_2\' - v_1\'}{v_1 - v_2}',
            description: 'Ratio of relative velocities after and before collision',
        },
        {
            id: 'momentum',
            name: 'Conservation of Momentum',
            latex: 'm_1v_1 + m_2v_2 = m_1v_1\' + m_2v_2\'',
            description: 'Total momentum is conserved in collision',
        },
    ],
    derivatives: (state, params) => {
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const v1 = getState(state, 'v1');
        const v2 = getState(state, 'v2');
        const collided = getState(state, 'collided');
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const e = getParam(params, 'e');
        const radius = getParam(params, 'radius');
        const distance = x2 - x1;
        const collisionThreshold = 2 * radius;
        if (distance <= collisionThreshold && v1 > v2 && collided < 0.5) {
            const v1_f = (m1 * v1 + m2 * v2 - m2 * e * (v1 - v2)) / (m1 + m2);
            const v2_f = (m1 * v1 + m2 * v2 + m1 * e * (v1 - v2)) / (m1 + m2);
            return {
                x1: v1_f,
                x2: v2_f,
                v1: 0,
                v2: 0,
                collided: 1,
            };
        }
        const resetCollision = collided > 0.5 && distance > collisionThreshold * 1.1;
        return {
            x1: v1,
            x2: v2,
            v1: 0,
            v2: 0,
            collided: resetCollision ? 0 : collided,
        };
    },
    energyComputation: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const v1 = getState(state, 'v1');
        const v2 = getState(state, 'v2');
        const ke = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v2 * v2;
        return { kinetic: ke, potential: 0, thermal: 0, total: ke };
    },
    failureModes: [
        {
            id: 'invalid-e',
            name: 'Invalid Restitution Coefficient',
            check: (_state, params) => {
                const e = getParam(params, 'e');
                return e < 0 || e > 1;
            },
            severity: 'error',
            educationalMessage: 'The coefficient of restitution must be between 0 (perfectly inelastic) and 1 (perfectly elastic).',
        },
    ],
    bounds: {
        x1: { min: -10, max: 10 },
        x2: { min: -10, max: 10 },
        v1: { min: -20, max: 20 },
        v2: { min: -20, max: 20 },
    },
});
// ============================================================================
// 2D COLLISION
// ============================================================================
export const Collision2D = createExperimentFromSchema({
    id: 'collision-2d',
    name: '2D Collision',
    domain: 'physics',
    description: 'Elastic collision between two objects in two dimensions',
    conceptTags: ['Conservation of Momentum', 'Vector Analysis', '2D Kinematics'],
    difficulty: 6,
    stateVariables: [
        { id: 'x1', name: 'Object 1 X Position', unit: 'm', initial: -2, symbol: 'x_1' },
        { id: 'y1', name: 'Object 1 Y Position', unit: 'm', initial: -0.5, symbol: 'y_1' },
        { id: 'x2', name: 'Object 2 X Position', unit: 'm', initial: 0, symbol: 'x_2' },
        { id: 'y2', name: 'Object 2 Y Position', unit: 'm', initial: 0.5, symbol: 'y_2' },
        { id: 'vx1', name: 'Object 1 X Velocity', unit: 'm/s', initial: 2, symbol: 'v_{x1}' },
        { id: 'vy1', name: 'Object 1 Y Velocity', unit: 'm/s', initial: 0.5, symbol: 'v_{y1}' },
        { id: 'vx2', name: 'Object 2 X Velocity', unit: 'm/s', initial: -1, symbol: 'v_{x2}' },
        { id: 'vy2', name: 'Object 2 Y Velocity', unit: 'm/s', initial: -0.5, symbol: 'v_{y2}' },
        { id: 'collided', name: 'Collision Flag', unit: '', initial: 0 },
    ],
    parameters: [
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm_1' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 0.1, max: 10, default: 1.5, symbol: 'm_2' },
        { id: 'radius', name: 'Object Radius', unit: 'm', min: 0.01, max: 1, default: 0.15, symbol: 'R' },
    ],
    equations: [
        {
            id: '2d-momentum',
            name: '2D Momentum Conservation',
            latex: 'm_1\\vec{v}_1 + m_2\\vec{v}_2 = m_1\\vec{v}_1\' + m_2\\vec{v}_2\'',
            description: 'Vector momentum is conserved in both x and y directions',
        },
        {
            id: 'impulse-direction',
            name: 'Impulse Direction',
            latex: '\\vec{J} \\parallel \\hat{n}',
            description: 'Impulse acts along the line connecting centers (normal direction)',
        },
    ],
    derivatives: (state, params) => {
        const x1 = getState(state, 'x1');
        const y1 = getState(state, 'y1');
        const x2 = getState(state, 'x2');
        const y2 = getState(state, 'y2');
        const vx1 = getState(state, 'vx1');
        const vy1 = getState(state, 'vy1');
        const vx2 = getState(state, 'vx2');
        const vy2 = getState(state, 'vy2');
        const collided = getState(state, 'collided');
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const radius = getParam(params, 'radius');
        const dx = x2 - x1;
        const dy = y2 - y1;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const collisionThreshold = 2 * radius;
        const dvx = vx1 - vx2;
        const dvy = vy1 - vy2;
        const approaching = (dvx * dx + dvy * dy) > 0;
        if (dist <= collisionThreshold && approaching && collided < 0.5) {
            const nx = dx / dist;
            const ny = dy / dist;
            const dv1n = vx1 * nx + vy1 * ny;
            const dv2n = vx2 * nx + vy2 * ny;
            const v1n_f = ((m1 - m2) * dv1n + 2 * m2 * dv2n) / (m1 + m2);
            const v2n_f = (2 * m1 * dv1n + (m2 - m1) * dv2n) / (m1 + m2);
            const tx = -ny;
            const ty = nx;
            const dv1t = vx1 * tx + vy1 * ty;
            const dv2t = vx2 * tx + vy2 * ty;
            const vx1_f = v1n_f * nx + dv1t * tx;
            const vy1_f = v1n_f * ny + dv1t * ty;
            const vx2_f = v2n_f * nx + dv2t * tx;
            const vy2_f = v2n_f * ny + dv2t * ty;
            return {
                x1: vx1_f,
                y1: vy1_f,
                x2: vx2_f,
                y2: vy2_f,
                vx1: 0,
                vy1: 0,
                vx2: 0,
                vy2: 0,
                collided: 1,
            };
        }
        const resetCollision = collided > 0.5 && dist > collisionThreshold * 1.1;
        return {
            x1: vx1,
            y1: vy1,
            x2: vx2,
            y2: vy2,
            vx1: 0,
            vy1: 0,
            vx2: 0,
            vy2: 0,
            collided: resetCollision ? 0 : collided,
        };
    },
    energyComputation: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const vx1 = getState(state, 'vx1');
        const vy1 = getState(state, 'vy1');
        const vx2 = getState(state, 'vx2');
        const vy2 = getState(state, 'vy2');
        const v1sq = vx1 * vx1 + vy1 * vy1;
        const v2sq = vx2 * vx2 + vy2 * vy2;
        const ke = 0.5 * m1 * v1sq + 0.5 * m2 * v2sq;
        return { kinetic: ke, potential: 0, thermal: 0, total: ke };
    },
    bounds: {
        x1: { min: -10, max: 10 },
        y1: { min: -10, max: 10 },
        x2: { min: -10, max: 10 },
        y2: { min: -10, max: 10 },
    },
});
// ============================================================================
// PULLEY SYSTEM WITH FRICTION
// ============================================================================
export const PulleyWithFriction = createExperimentFromSchema({
    id: 'pulley-with-friction',
    name: 'Pulley System with Friction',
    domain: 'physics',
    description: 'Atwood machine with friction in the pulley bearing',
    conceptTags: ['Torque', 'Rotational Dynamics', 'Friction Torque'],
    difficulty: 6,
    stateVariables: [
        { id: 'y1', name: 'Mass 1 Position', unit: 'm', initial: 0, symbol: 'y_1' },
        { id: 'v1', name: 'Velocity', unit: 'm/s', initial: 0, symbol: 'v' },
    ],
    parameters: [
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 0.1, max: 10, default: 2, symbol: 'm_1' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 0.1, max: 10, default: 3, symbol: 'm_2' },
        { id: 'Rp', name: 'Pulley Radius', unit: 'm', min: 0.05, max: 0.5, default: 0.1, symbol: 'R_p' },
        { id: 'Ip', name: 'Pulley Moment of Inertia', unit: 'kg·m²', min: 0.001, max: 1, default: 0.01, symbol: 'I_p' },
        { id: 'tau_f', name: 'Friction Torque', unit: 'N·m', min: 0, max: 1, default: 0.05, symbol: '\\tau_f' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'effective-mass',
            name: 'Effective Mass with Rotational Inertia',
            latex: 'm_{eff} = m_1 + m_2 + \\frac{I_p}{R_p^2}',
            description: 'Total effective mass including pulley rotational inertia',
        },
        {
            id: 'net-force',
            name: 'Net Force',
            latex: 'F_{net} = (m_2 - m_1)g - \\frac{\\tau_f}{R_p}\\cdot\\text{sgn}(v)',
            description: 'Net force accounting for friction torque',
        },
    ],
    derivatives: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const g = getParam(params, 'g');
        const Rp = getParam(params, 'Rp');
        const Ip = getParam(params, 'Ip');
        const tau_f = getParam(params, 'tau_f');
        const v1 = getState(state, 'v1');
        const m_eff = m1 + m2 + Ip / (Rp * Rp);
        const sign_v = v1 >= 0 ? 1 : -1;
        const F_net = (m2 - m1) * g - (tau_f / Rp) * sign_v;
        const F_gravity = (m2 - m1) * g;
        const F_friction = tau_f / Rp;
        let a = 0;
        if (Math.abs(v1) < 0.001 && Math.abs(F_gravity) <= F_friction) {
            a = 0;
        }
        else {
            a = F_net / m_eff;
        }
        return {
            y1: v1,
            v1: a,
        };
    },
    energyComputation: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const g = getParam(params, 'g');
        const Rp = getParam(params, 'Rp');
        const Ip = getParam(params, 'Ip');
        const y1 = getState(state, 'y1');
        const v1 = getState(state, 'v1');
        const y2 = -y1;
        const omega = v1 / Rp;
        const ke_trans = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v1 * v1;
        const ke_rot = 0.5 * Ip * omega * omega;
        const pe = m1 * g * y1 + m2 * g * y2;
        return {
            kinetic: ke_trans + ke_rot,
            potential: pe,
            thermal: 0,
            total: ke_trans + ke_rot + pe,
        };
    },
    failureModes: [
        {
            id: 'stuck-pulley',
            name: 'Pulley Stuck',
            check: (state, params) => {
                const m1 = getParam(params, 'm1');
                const m2 = getParam(params, 'm2');
                const g = getParam(params, 'g');
                const Rp = getParam(params, 'Rp');
                const tau_f = getParam(params, 'tau_f');
                const stateValues = getStateValues(state);
                const v1 = getState(stateValues, 'v1');
                const F_gravity = Math.abs(m2 - m1) * g;
                const F_friction = tau_f / Rp;
                return Math.abs(v1) < 0.001 && F_gravity <= F_friction;
            },
            severity: 'warning',
            educationalMessage: 'Friction torque is too large for the mass difference to overcome. The pulley will not rotate.',
        },
    ],
});
// ============================================================================
// BLOCK ON INCLINE CONNECTED TO HANGING MASS
// ============================================================================
export const InclineHangingMass = createExperimentFromSchema({
    id: 'incline-hanging-mass',
    name: 'Block on Incline with Hanging Mass',
    domain: 'physics',
    description: 'Connected block on incline and hanging mass over pulley',
    conceptTags: ['Constraint Forces', 'Tension', 'Inclined Plane'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Block Position on Slope', unit: 'm', initial: 0, symbol: 'x' },
        { id: 'v', name: 'System Velocity', unit: 'm/s', initial: 0, symbol: 'v', description: 'Positive = block moves up slope, hanging mass moves down' },
    ],
    parameters: [
        { id: 'm1', name: 'Block Mass', unit: 'kg', min: 0.1, max: 10, default: 2, symbol: 'm_1', description: 'Mass on incline' },
        { id: 'm2', name: 'Hanging Mass', unit: 'kg', min: 0.1, max: 10, default: 1.5, symbol: 'm_2' },
        { id: 'theta', name: 'Incline Angle', unit: 'rad', min: 0, max: Math.PI / 2, default: Math.PI / 4, symbol: '\\theta' },
        { id: 'mu', name: 'Friction Coefficient', unit: '', min: 0, max: 1, default: 0.2, symbol: '\\mu' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'acceleration',
            name: 'System Acceleration',
            latex: 'a = \\frac{m_2g - m_1g\\sin\\theta - \\mu m_1g\\cos\\theta}{m_1 + m_2}',
            description: 'Acceleration when moving (hanging mass pulling up)',
        },
    ],
    derivatives: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const theta = getParam(params, 'theta');
        const mu = getParam(params, 'mu');
        const g = getParam(params, 'g');
        const v = getState(state, 'v');
        const sinTheta = Math.sin(theta);
        const cosTheta = Math.cos(theta);
        const F_gravity_hanging = m2 * g;
        const F_gravity_incline = m1 * g * sinTheta;
        const F_friction_max = mu * m1 * g * cosTheta;
        let F_friction = 0;
        if (v > 0.001) {
            F_friction = F_friction_max;
        }
        else if (v < -0.001) {
            F_friction = -F_friction_max;
        }
        else {
            const F_net_no_friction = F_gravity_hanging - F_gravity_incline;
            if (Math.abs(F_net_no_friction) <= F_friction_max) {
                F_friction = -F_net_no_friction;
            }
            else {
                F_friction = F_friction_max * Math.sign(F_net_no_friction);
            }
        }
        const F_net = F_gravity_hanging - F_gravity_incline - F_friction;
        const a = F_net / (m1 + m2);
        return {
            x: v,
            v: a,
        };
    },
    energyComputation: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const theta = getParam(params, 'theta');
        const g = getParam(params, 'g');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const h1 = x * Math.sin(theta);
        const h2 = -x;
        const ke = 0.5 * (m1 + m2) * v * v;
        const pe1 = m1 * g * h1;
        const pe2 = m2 * g * h2;
        return {
            kinetic: ke,
            potential: pe1 + pe2,
            thermal: 0,
            total: ke + pe1 + pe2,
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        v: { min: -10, max: 10 },
    },
});
// ============================================================================
// FRICTION BLOCK ON MOVING SURFACE
// ============================================================================
export const BlockOnMovingSurface = createExperimentFromSchema({
    id: 'block-on-moving-surface',
    name: 'Block on Moving Surface',
    domain: 'physics',
    description: 'Block on surface moving with constant velocity, with friction',
    conceptTags: ['Friction', 'Relative Motion', 'Kinetic Friction'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Block Position', unit: 'm', initial: 0, symbol: 'x', description: 'Position relative to ground' },
        { id: 'v', name: 'Block Velocity', unit: 'm/s', initial: 0, symbol: 'v', description: 'Velocity relative to ground' },
    ],
    parameters: [
        { id: 'V_surface', name: 'Surface Velocity', unit: 'm/s', min: -5, max: 5, default: 2, symbol: 'V_s', description: 'Velocity of moving surface' },
        { id: 'mu_k', name: 'Kinetic Friction', unit: '', min: 0, max: 1, default: 0.3, symbol: '\\mu_k' },
        { id: 'mu_s', name: 'Static Friction', unit: '', min: 0, max: 1, default: 0.5, symbol: '\\mu_s' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'friction-force',
            name: 'Kinetic Friction Force',
            latex: 'f_k = \\mu_k mg \\cdot \\text{sgn}(v_{rel})',
            description: 'Friction opposes relative motion between surfaces',
        },
        {
            id: 'max-static',
            name: 'Maximum Static Friction',
            latex: 'f_{s,max} = \\mu_s mg',
            description: 'Maximum static friction before slipping occurs',
        },
    ],
    derivatives: (state, params) => {
        const mu_k = getParam(params, 'mu_k');
        const g = getParam(params, 'g');
        const V_s = getParam(params, 'V_surface');
        const v = getState(state, 'v');
        const v_rel = v - V_s;
        const f_kinetic = mu_k * g;
        let a = 0;
        if (Math.abs(v - V_s) < 0.001) {
            a = 0;
        }
        else if (Math.abs(v_rel) > 0.001) {
            a = -f_kinetic * Math.sign(v_rel);
        }
        return {
            x: v,
            v: a,
        };
    },
    derivedState: (state, params) => {
        const V_s = getParam(params, 'V_surface');
        const v = getState(state, 'v');
        return {
            v_rel: v - V_s,
        };
    },
    bounds: {
        x: { min: -10, max: 50 },
        v: { min: -10, max: 10 },
    },
});
// ============================================================================
// BANKED CURVE MOTION
// ============================================================================
export const BankedCurveMotion = createExperimentFromSchema({
    id: 'banked-curve-motion',
    name: 'Banked Curve Motion',
    domain: 'physics',
    description: 'Vehicle on a banked curve with and without friction',
    conceptTags: ['Circular Motion', 'Banked Curves', 'Centripetal Force'],
    difficulty: 6,
    stateVariables: [
        { id: 'theta', name: 'Angular Position', unit: 'rad', initial: 0, symbol: '\\theta', description: 'Position around the curve' },
        { id: 'v', name: 'Vehicle Speed', unit: 'm/s', initial: 15, symbol: 'v', description: 'Tangential speed' },
    ],
    parameters: [
        { id: 'R', name: 'Curve Radius', unit: 'm', min: 10, max: 200, default: 50, symbol: 'R' },
        { id: 'phi', name: 'Bank Angle', unit: 'rad', min: 0, max: Math.PI / 3, default: Math.PI / 6, symbol: '\\phi' },
        { id: 'mu', name: 'Friction Coefficient', unit: '', min: 0, max: 1, default: 0.4, symbol: '\\mu' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'ideal-speed',
            name: 'Ideal Speed (No Friction)',
            latex: 'v_{ideal} = \\sqrt{Rg\\tan\\phi}',
            description: 'Speed at which no friction is required',
        },
        {
            id: 'min-speed',
            name: 'Minimum Safe Speed',
            latex: 'v_{min} = \\sqrt{\\frac{Rg(\\sin\\phi - \\mu\\cos\\phi)}{\\cos\\phi + \\mu\\sin\\phi}}',
            description: 'Minimum speed to prevent sliding down',
        },
        {
            id: 'max-speed',
            name: 'Maximum Safe Speed',
            latex: 'v_{max} = \\sqrt{\\frac{Rg(\\sin\\phi + \\mu\\cos\\phi)}{\\cos\\phi - \\mu\\sin\\phi}}',
            description: 'Maximum speed before sliding up',
        },
    ],
    derivatives: (state, params) => {
        const R = getParam(params, 'R');
        const v = getState(state, 'v');
        const omega = v / R;
        return {
            theta: omega,
            v: 0,
        };
    },
    derivedState: (state, params) => {
        const R = getParam(params, 'R');
        const phi = getParam(params, 'phi');
        const mu = getParam(params, 'mu');
        const g = getParam(params, 'g');
        const v = getState(state, 'v');
        const v_ideal = Math.sqrt(R * g * Math.tan(phi));
        const sinPhi = Math.sin(phi);
        const cosPhi = Math.cos(phi);
        let v_min = 0;
        let v_max = Infinity;
        if (cosPhi + mu * sinPhi > 0) {
            v_min = Math.sqrt(R * g * Math.max(0, sinPhi - mu * cosPhi) / (cosPhi + mu * sinPhi));
        }
        if (cosPhi - mu * sinPhi > 0) {
            v_max = Math.sqrt(R * g * (sinPhi + mu * cosPhi) / (cosPhi - mu * sinPhi));
        }
        const a_c = v * v / R;
        return {
            v_ideal,
            v_min,
            v_max,
            centripetal_acceleration: a_c,
            g_force: a_c / g + 1,
        };
    },
    energyComputation: (state, _params) => {
        const v = getState(state, 'v');
        const ke = 0.5 * v * v;
        return { kinetic: ke, potential: 0, thermal: 0, total: ke };
    },
    failureModes: [
        {
            id: 'too-slow',
            name: 'Too Slow - Slide Down',
            check: (state, params) => {
                const phi = getParam(params, 'phi');
                const mu = getParam(params, 'mu');
                const R = getParam(params, 'R');
                const g = getParam(params, 'g');
                const stateValues = getStateValues(state);
                const v = getState(stateValues, 'v');
                const sinPhi = Math.sin(phi);
                const cosPhi = Math.cos(phi);
                const v_min = Math.sqrt(R * g * Math.max(0, sinPhi - mu * cosPhi) / (cosPhi + mu * sinPhi));
                return v < v_min && cosPhi + mu * sinPhi > 0;
            },
            severity: 'warning',
            educationalMessage: 'The vehicle is too slow and will slide down the banked curve. Increase speed or bank angle.',
        },
        {
            id: 'too-fast',
            name: 'Too Fast - Slide Up',
            check: (state, params) => {
                const phi = getParam(params, 'phi');
                const mu = getParam(params, 'mu');
                const R = getParam(params, 'R');
                const g = getParam(params, 'g');
                const stateValues = getStateValues(state);
                const v = getState(stateValues, 'v');
                const sinPhi = Math.sin(phi);
                const cosPhi = Math.cos(phi);
                if (cosPhi - mu * sinPhi <= 0)
                    return false;
                const v_max = Math.sqrt(R * g * (sinPhi + mu * cosPhi) / (cosPhi - mu * sinPhi));
                return v > v_max;
            },
            severity: 'warning',
            educationalMessage: 'The vehicle is too fast and will slide up the banked curve. Reduce speed.',
        },
    ],
    bounds: {
        theta: { min: -Infinity, max: Infinity },
        v: { min: 0, max: 50 },
    },
});
//# sourceMappingURL=mechanics.js.map